import Inputmask from 'inputmask';
import cloneDeep from 'lodash/cloneDeep';
import { Directive, ElementRef, AfterViewInit, Input, HostBinding, OnChanges, SimpleChanges } from '@angular/core';

export interface IInputMaskArgs {
  countryCode?: string;
  letterSpacing?: number;
}

@Directive({
  standalone: false,
  selector: '[inputMask]',
  exportAs: 'inputMaskDirective',
})
export class InputMaskDirective implements AfterViewInit, OnChanges {
  @HostBinding('class.ng-invalid') isValid = false;

  @Input() inputMask: string;
  @Input() inputMaskArgs: IInputMaskArgs = {};

  private postNumberPlaceholder = '';

  private masks: any = {
    noSpacesText: {
      regex: '^[^\\s]+$',
      placeholder: '',
    },
    decimal: {
      alias: 'decimal',
      radixPoint: ',',
      groupSeparator: ' ',
      allowMinus: false,
      autoGroup: true,
      onBeforeMask: (value) => {
        return value.replace(/\./g, ',');
      },
    },
    percentage: {
      alias: 'decimal',
      max: 100,
      min: 0,
      onBeforeMask: (value) => {
        return String(value).replace(/\./g, ',');
      },
      onUnMask: (value) => {
        return String(value).replace(/,/g, '.');
      },
    },
    commission: {
      regex: '\\d(\\.\\d{1,2})?',
    },
    vat: {
      mask: '99[,99]',
      placeholder: '',
      greedy: false,
      allowMinus: false,
      onBeforeMask: (value) => {
        return value.replace(/\./g, ',');
      },
      onUnMask: (value) => {
        return value.replace(/,/g, '.');
      },
    },
    number: {
      regex: '0|[1-9][0-9]*',
      placeholder: '0',
    },
    positiveIntegers: {
      regex: '^[1-9]\\d*$',
      placeholder: '',
    },
    numberWithoutPlaceholder: {
      regex: '0|[1-9][0-9]*',
      placeholder: '',
    },
    numberDividedByDash: {
      regex: '^([1-9]+\\d*(-[1-9]+\\d*)?)$',
      placeholder: '',
      onUnMask: (maskedValue) => {
        return maskedValue;
      },
    },
    integer: {
      alias: 'decimal',
      radixPoint: ',',
      groupSeparator: ' ',
      allowMinus: true,
      autoGroup: true,
      onBeforeMask: (value) => {
        return String(value).replace(/\./g, ',');
      },
    },
    decimalExtended: {
      alias: 'decimal',
      radixPoint: ',',
      groupSeparator: ' ',
      allowMinus: false,
      autoGroup: true,
      onBeforeMask: (value) => {
        return String(value).replace(/\./g, ',');
      },
    },
    time: {
      placeholder: '-',
      regex: '([0-1]?[0-9]|2[0-3]):[0-5][0-9]',
      onUnMask: (maskedValue) => {
        return maskedValue;
      },
    },
    orgNumber: {
      mask: '999 999 999',
      onUnMask: (value, unmaskedValue) => {
        // to keep spaces
        return value;
      },
      placeholder: '',
    },
    phone: {
      regex: '[+\\d\\s\\(\\)-]+',
      placeholder: '',
      keepStatic: true,
    },
    phoneNotIncludingPlus: {
      regex: '[\\d\\s\\(\\)-]+',
      placeholder: '',
      keepStatic: true,
      onUnMask: (maskedValue) => {
        return maskedValue.replaceAll(' ', '');
      },
    },
    phoneIncludingPlus: {
      regex: '[+\\d\\s\\(\\)-]+',
      placeholder: '',
      keepStatic: true,
      onUnMask: (maskedValue) => {
        return maskedValue.replaceAll(' ', '');
      },
    },
    postNumber: {
      mask: '9999',
      placeholder: '',
    },
    pinsNumber: {
      mask: '9999',
      placeholder: '',
    },
    customerId: {
      mask: '9999-99999',
      placeholder: 'XXXX-XXXXX',
      onUnMask: (value, unmaskedValue) => {
        // to keep spaces
        return value;
      },
    },
  };

  private defaultOptions: any = {
    autoUnmask: true,
    unmaskAsNumber: true,
    rightAlign: false,
    onincomplete: () => {
      this.isValid = false;
    },
    oncomplete: () => {
      this.isValid = false;
    },
  };

  constructor(private el: ElementRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['inputMaskArgs']) {
      this.createInputMaskInstance();
    }
  }

  ngAfterViewInit() {
    this.createInputMaskInstance();
  }

  public getOptions() {
    const localized = cloneDeep(this.masks);
    if (this.inputMaskArgs.countryCode) {
      switch (this.inputMaskArgs.countryCode.toLowerCase()) {
        case 'se':
          localized.postNumber = {
            mask: '999 99',
            placeholder: this.postNumberPlaceholder,

            onUnMask: (maskedValue) => {
              const regex = new RegExp(this.postNumberPlaceholder, 'g');
              // to keep space
              return maskedValue.replace(regex, '').trim();
            },
          };

          Object.assign(localized.orgNumber, { mask: '999999-9999' });
          break;
        default:
          break;
      }
    }

    if (this.inputMaskArgs.letterSpacing) {
      localized.pinsNumber = {
        mask: this._makeSpacingMask(this.inputMaskArgs.letterSpacing),
        onUnMask: (maskedValue) => {
          return maskedValue.replaceAll(' ', '');
        },
      };
    }

    return Object.assign({}, this.defaultOptions, localized[this.inputMask] || {});
  }

  public createInputMaskInstance(): void {
    const options = this.getOptions();

    if (options) {
      Inputmask(options).mask(this.el.nativeElement);
    }
  }

  private _makeSpacingMask(spacing: number) {
    let strSpacing = '';
    for (let i = 0; i <= spacing; i++) {
      strSpacing += ' ';
    }
    return ['9', '9', '9', '9'].join(strSpacing);
  }
}
